import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { finalize, switchMap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { AuthenticationMethod, DateFormatEnum, LocaleInput } from '@app/models';
import { ErrorHandlerService, AuthenticationService, RegistrationContext, LoginContext, OAuthService, AppService, NotyService, UserService } from '@app/services';
import { I18nService } from '@app/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-auth-register',
  templateUrl: './auth-register.component.html',
  styleUrls: ['../auth.scss', './auth-register.component.scss'],
  standalone: false
})
export class AuthRegisterComponent implements OnInit {
  form!: UntypedFormGroup;
  isLoading = false;
  oauthForm!: UntypedFormGroup;

  readonly AuthenticationMethod = AuthenticationMethod;

  constructor(
    public route: ActivatedRoute,
    public i18nService: I18nService,
    public appService: AppService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private authenticationService: AuthenticationService,
    private oauthService: OAuthService,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private translateService: TranslateService
  ) {
    this.oauthForm = this.formBuilder.group({ email: this.route.snapshot.queryParams.email || '', password: '', referral: this.route.snapshot.queryParams.r } as LoginContext);
    this.form = this.formBuilder.group({
      email: this.route.snapshot.queryParams.email || '',
      password: '',
      referral: this.route.snapshot.queryParams.r || null
    } as RegistrationContext);
  }

  ngOnInit() {
    this.authenticationService.logout().subscribe();
  }

  sendEmailVerificationPrompt() {
    this.isLoading = true;
    this.form.markAllAsTouched();

    this.authenticationService
      .sendEmailVerificationPrompt({ email: this.form.get('email').value, locale: this.i18nService.prepareLocaleInput() })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => {
          this.router.navigate(['/confirmacao'], {
            state: {
              email: this.form.get('email').value,
              password: this.form.get('password').value,
              referral: this.oauthForm.get('referral').value,
              fromAuthRegister: true,
              redirectUrl: this.route.snapshot.queryParams.redirect || null
            }
          });
        },
        error: error => {
          if (error?.message === 'email_already_verified') {
            this.notyService.error(this.translateService.instant('error_already_registered'));
          } else {
            this.errorHandlerService.handleValidation(this.form, error);
          }
        }
      });
  }

  login(method: AuthenticationMethod = AuthenticationMethod.Email, additionalInfo?: any) {
    this.isLoading = true;
    if (method === AuthenticationMethod.Email) {
      this.oauthForm.markAllAsTouched();
    }

    this.oauthService.login(method, { ...(additionalInfo || {}), stopAutoLogin: true }).subscribe({
      next: oauth => {
        this.authenticationService
          .login(oauth ? { code: oauth.accessToken, referral: this.oauthForm.get('referral').value } : this.oauthForm.value, method)
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe({
            next: () => this.router.navigate([this.route.snapshot.queryParams.redirect || '/'], { replaceUrl: true, queryParams: this.route.snapshot.queryParams }),
            error: error => {
              if (error && error.status === 424) {
                const redirect = this.route.snapshot.queryParams.redirect;
                this.router.navigate(['/confirmacao'], { queryParams: { email: this.oauthForm.get('email').value, ...(redirect ? { redirect } : {}) } });
              } else {
                this.errorHandlerService.handleValidation(this.oauthForm, error);
              }
            }
          });
      },
      error: () => (this.isLoading = false)
    });
  }

  shouldShowOauth() {
    return !environment.electron && this.appService.isAutentiqueDomain();
  }
}
